import '../../css/Calandar.css';
import { Link } from 'react-router-dom';
import { useUser } from '../../hooks/useUser';
import { useMemo, useState } from 'react';
import { EventDetails, UserProfile } from '../../interfaces/userInterfaces';
import { CalendarEventModal } from './CalendarViews/CalendarEventModal';
import {
  MONTHS,
  WEEK_DAYS,
  buildCalendarGrid,
  buildEventDateSet,
} from './CalendarHelpers/calendarUtils';

interface CalendarProps {
  useCase: string;
  profile: UserProfile;
}

export const Calendar: React.FC<CalendarProps> = ({ useCase, profile }) => {
  const { userAttendingEventsList } = useUser();
  const today = useMemo(() => new Date(), []);

  // ── Navigation state ───────────────────────────────────────────────────────
  const [year, setYear] = useState(today.getFullYear());
  const [monthIndex, setMonthIndex] = useState(today.getMonth());

  // ── Modal state ────────────────────────────────────────────────────────────
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  // ── Derived data — no useEffect needed ────────────────────────────────────
  const eventList: EventDetails[] = useMemo(() => {
    const collapsed = profile?.attending_event?.map((ae) => ae.event) ?? [];
    return collapsed.filter((e): e is EventDetails => Boolean(e?.user_id && e?.event_name));
  }, [profile]);

  const calendarGrid = useMemo(() => buildCalendarGrid(year, monthIndex), [year, monthIndex]);

  const eventDateMap = useMemo(() => buildEventDateSet(eventList), [eventList]);

  // ── Navigation handlers ────────────────────────────────────────────────────
  const shiftMonth = (delta: number) => {
    setMonthIndex((prev) => {
      const next = prev + delta;
      if (next < 0) {
        setYear((y) => y - 1);
        return 11;
      }
      if (next > 11) {
        setYear((y) => y + 1);
        return 0;
      }
      return next;
    });
  };

  const shiftYear = (delta: number) => setYear((y) => y + delta);

  // ── Render ─────────────────────────────────────────────────────────────────
  const currentMonth = MONTHS[monthIndex];

  return (
    <>
      <table className="calendar-table">
        <caption id="calendar-caption">
          <span className="carot-calendar-caption year" onClick={() => shiftYear(-1)}>
            {'<<'}
          </span>
          <span className="carot-calendar-caption month" onClick={() => shiftMonth(-1)}>
            {'<'}
          </span>
          {currentMonth.month} {year}
          <span className="carot-calendar-caption month" onClick={() => shiftMonth(1)}>
            {'>'}
          </span>
          <span className="carot-calendar-caption year" onClick={() => shiftYear(1)}>
            {'>>'}
          </span>
        </caption>

        <thead>
          <tr>
            {WEEK_DAYS.map((day) => (
              <th key={day} className="day-name">
                {day}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {calendarGrid.map((week, wi) => (
            <tr key={`week-${wi}`}>
              {week.map((date, di) => {
                if (!date) {
                  return <td key={`empty-${wi}-${di}`} className="day-cell before-month" />;
                }

                const isToday = date.toLocaleDateString() === today.toLocaleDateString();
                const isWeekend = di === 0 || di === 6;
                const dateKey = date.toLocaleDateString();
                const dayEvents = eventDateMap.get(dateKey) ?? [];

                const cellClass = [
                  'day-cell',
                  isToday ? 'today' : '',
                  isWeekend ? 'weekend-calendar-cell' : 'weekday-calendar-cell',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <td
                    key={dateKey}
                    id={`event-date-${dateKey}`}
                    className={cellClass}
                    onClick={() => setSelectedDate(date)}
                  >
                    <span>{date.getDate()}</span>
                    {dayEvents.map((event) => (
                      <span key={event.id} className="event-scheduled">
                        {event.event_name}
                      </span>
                    ))}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <h3>Select a date above to view or create events</h3>
      {eventList.length > 0 && (
        <ul>
          {eventList.reverse().map((event) => (
            <li key={`event-list-${event.id}`} style={{ listStyle: 'none' }}>
              <Link
                to={`../event/${event.id}/${userAttendingEventsList?.includes(event.id)}`}
                className="calendar-list-item"
              >
                {event.event_name} : {event.date}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {selectedDate && (
        <CalendarEventModal
          eventDate={selectedDate}
          eventList={eventList}
          useCase={useCase}
          setSelectedDate={setSelectedDate}
        />
      )}
    </>
  );
};
